import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useHistory } from "react-router";

import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import { capitalize } from "helpers/Helpers";

import { PostInvoice } from "api/rpc/2024-04/facilityAdmin/order/invoice";
import { GetCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetEvent } from "api/rpc/2024-04/facilityAdmin/event/hospitalityEvent";
import { StatusCode } from "api/protocols";

import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Icon from "components/icon/Icon";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import { Select } from "components/select/index";
import { ICustomer } from "redux/reducers/models/customer";
import { ITournament } from "redux/reducers/models/tournament";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { useTranslation } from "react-i18next";

interface IInvoiceState {
  customer_id: number;
  customer_billing_address_id: number;
  customer_shipping_address_id: number;
  date: Date;
  selected_event: any;
  invoice_due: string;
}

interface IFilterState {
  eventQuery: string;
  selectedEvent: ITournament;
  eventSearching: boolean;
  customerQuery: string;
  selectedCustomer: ICustomer;
  customerSearching: boolean;
}

export default function InvoiceNew() {
  const { Option } = Select;
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const [events, setEvents] = useState<Array<any>>(undefined);
  const [customers, setCustomers] = useState<Array<ICustomer>>([]);

  const [invoiceState, setInvoiceState] = useState<IInvoiceState>({
    customer_id: null,
    customer_billing_address_id: null,
    customer_shipping_address_id: null,
    date: new Date(),
    selected_event: null,
    invoice_due: null,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    eventQuery: "",
    selectedEvent: null,
    eventSearching: false,
    customerQuery: "",
    selectedCustomer: null,
    customerSearching: false,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadEvents(source.token);
    return () => {
      source.cancel();
    };
  }, [filterState.eventQuery]);

  useEffect(() => {
    const source = axios.CancelToken.source();
    void loadCustomers(source.token);
    return () => {
      source.cancel();
    };
  }, [filterState.customerQuery]);

  async function loadEvents(token: CancelToken) {
    let tournaments: Array<any> = [];
    let events: Array<any> = [];
    setEvents(null);

    const tournamentRes = await GetTournament({ search: filterState.eventQuery }, false, token);

    if (token && token.reason) {
      return;
    }
    if (tournamentRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.001")));
    } else {
      tournaments = tournamentRes.data.map(tournament => ({ ...tournament, event_type: "tournament" }));
    }

    const eventRes = await GetEvent({ search: filterState.eventQuery }, false, token);

    if (token && token.reason) {
      return;
    }
    if (eventRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.002")));
    } else {
      events = eventRes.data.map(event => ({ ...event, event_type: "hospitality" }));
    }

    const allEvents = tournaments.concat(events);

    setEvents(allEvents);
  }

  async function loadCustomers(token?: CancelToken) {
    setFilterState(prevState => ({ ...prevState, customerSearching: true }));
    setCustomers(undefined);

    const customerRes = await GetCustomer(
      {
        search: filterState.customerQuery,
        "address-lines": true,
      },
      false,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.003")));
      return;
    }

    setCustomers(customerRes.data);
    setFilterState(prevState => ({ ...prevState, customerSearching: false }));
  }

  async function createInvoice() {
    console.log("Selected Event", invoiceState?.selected_event);
    const invoiceRes = await PostInvoice(
      {
        customer_id: invoiceState?.customer_id,
        customer_billing_address_id:
          invoiceState?.customer_billing_address_id === -1 ? null : invoiceState?.customer_billing_address_id,
        customer_shipping_address_id:
          invoiceState?.customer_shipping_address_id === -1 ? null : invoiceState?.customer_shipping_address_id,
        date: invoiceState?.date.toISOString().slice(0, 10),
        tournament_id:
          invoiceState?.selected_event?.event_type === "tournament" ? invoiceState?.selected_event?.id : null,
        hospitality_event_id:
          invoiceState?.selected_event?.event_type === "hospitality" ? invoiceState?.selected_event?.id : null,
        invoice_due: invoiceState?.invoice_due,
      },
      true,
    );

    if (invoiceRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.order.invoices.invoice_new.004")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.order.invoices.invoice_new.005")));
    history.push(`/admin/invoices/${String(invoiceRes.data.id)}/edit`);
  }

  function handleEventChange(event?: any) {
    if (event) {
      setInvoiceState(prevState => ({ ...prevState, selected_event: event }));
      setFilterState(prevState => ({
        ...prevState,
        eventQuery: "",
        selectedEvent: event,
      }));
    }
  }

  function handleCustomerChange(value: any, id: string, customer?: ICustomer) {
    if (customer) {
      setInvoiceState(prevState => ({ ...prevState, [id]: customer.id === -1 ? null : customer.id }));
      setFilterState(prevState => ({ ...prevState, customerQuery: customer.full_name, selectedCustomer: customer }));
    }
  }

  function handleDatePickerChange(selectedDate: Date) {
    setInvoiceState(prevState => ({ ...prevState, date: selectedDate }));
  }

  function handleDropDownChange(value: any, property: string) {
    setInvoiceState(prev => ({ ...prev, [property]: value }));
  }

  const primaryAction = {
    content: t("secure.facility.order.invoices.invoice_new.006"),
    action: createInvoice,
  };

  return (
    <Page
      title={t("secure.facility.order.invoices.invoice_new.007")}
      primaryAction={primaryAction}
      breadcrumbs={[
        { prefix: true, label: t("secure.facility.order.invoices.invoice_new.008"), url: "/admin/invoices" },
      ]}
    >
      <Card>
        <Card.Section title={t("secure.facility.order.invoices.invoice_new.009")}>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <DatePickerInput
                  label={t("secure.facility.order.invoices.invoice_new.010")}
                  months={1}
                  position="left"
                  startingDate={invoiceState.date}
                  setStartingDate={handleDatePickerChange}
                />
                <Select
                  label={t("secure.facility.order.invoices.invoice_new.011")}
                  onChange={(value: string) => setInvoiceState(prevState => ({ ...prevState, invoice_due: value }))}
                >
                  <Option key={0} value={"on_receipt"}>
                    {t("secure.facility.order.invoices.invoice_new.012")}
                  </Option>
                  <Option key={1} value={"net_30"}>
                    {t("secure.facility.order.invoices.invoice_new.013")}
                  </Option>
                  <Option key={2} value={"net_60"}>
                    {t("secure.facility.order.invoices.invoice_new.014")}
                  </Option>
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                {invoiceState.selected_event ? (
                  <div>
                    <div>
                      <p className="event-label">{t("secure.facility.order.invoices.invoice_new.015")}</p>
                    </div>

                    <div className="selected-container">
                      <div className="event-name">
                        <div>{invoiceState.selected_event.name}</div>

                        <div>
                          <button
                            className=""
                            onClick={() => setInvoiceState(prevState => ({ ...prevState, selected_event: null }))}
                          >
                            <Icon icon={faTimes} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Select
                    showSearch
                    label={t("secure.facility.order.invoices.invoice_new.016")}
                    onSearch={(query: string) => setFilterState(prevState => ({ ...prevState, eventQuery: query }))}
                    onChange={(value: any, event: any) => handleEventChange(event)}
                    placeholder={t("secure.facility.order.invoices.invoice_new.017")}
                    allowClear
                    searchValue={filterState.eventQuery}
                    showDropDownOnFocus={true}
                    searching={!events}
                  >
                    <Option
                      value={-1}
                      extraValues={{ name: t("secure.facility.order.invoices.invoice_new.018"), id: -1 }}
                    >
                      <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_new.019")}</div>
                    </Option>
                    {events?.map((event: any, index: number) => {
                      return (
                        <Option value={event?.id} extraValues={event} key={index}>
                          {`${String(event.name)} (${String(capitalize(event.event_type))})`}
                        </Option>
                      );
                    })}
                  </Select>
                )}
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
        <Card.Section title={t("secure.facility.order.invoices.invoice_new.020")}>
          <Form>
            <FormLayout>
              <FormLayout.Group>
                <Select
                  showSearch
                  label={t("secure.facility.order.invoices.invoice_new.021")}
                  onSearch={(query: string) => setFilterState(prevState => ({ ...prevState, customerQuery: query }))}
                  onChange={(value: any, customer: ICustomer) => handleCustomerChange(value, "customer_id", customer)}
                  placeholder={t("secure.facility.order.invoices.invoice_new.022")}
                  searchValue={filterState.customerQuery}
                  showDropDownOnFocus={true}
                  searching={filterState.customerSearching}
                >
                  {customers?.map((customer: ICustomer, index: number) => {
                    return (
                      <Option value={customer?.id} extraValues={customer} key={index}>
                        <div className="text-semibold text-lg">{customer.full_name}</div>
                        <div className="text-sm">{customer.email}</div>
                      </Option>
                    );
                  })}
                </Select>
                <div />
              </FormLayout.Group>
              <FormLayout.Group>
                <Select
                  label={t("secure.facility.order.invoices.invoice_new.023")}
                  searchValue=""
                  onChange={(value: any) => handleDropDownChange(value, "customer_billing_address_id")}
                  showDropDownOnFocus={true}
                  disabled={!invoiceState.customer_id}
                >
                  <Option
                    value={-1}
                    extraValues={{ name: t("secure.facility.order.invoices.invoice_new.024"), id: -1 }}
                  >
                    <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_new.025")}</div>
                  </Option>
                  {filterState.selectedCustomer?.address_lines?.map((address: any, index: number) => {
                    return (
                      <Option value={address?.id} key={index}>
                        <div className="text-semibold text-lg">
                          {address.address_line_1}
                          <span className="text-sm">{` ${String(address.city)}, ${String(
                            address.province_name,
                          )}`}</span>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
                <Select
                  label={t("secure.facility.order.invoices.invoice_new.026")}
                  onChange={(value: any) => handleDropDownChange(value, "customer_shipping_address_id")}
                  showDropDownOnFocus={true}
                  disabled={!invoiceState.customer_id}
                >
                  <Option
                    value={-1}
                    extraValues={{ name: t("secure.facility.order.invoices.invoice_new.027"), id: -1 }}
                  >
                    <div className="text-semibold text-lg">{t("secure.facility.order.invoices.invoice_new.028")}</div>
                  </Option>
                  {filterState.selectedCustomer?.address_lines?.map((address: any, index: number) => {
                    return (
                      <Option value={address?.id} key={index}>
                        <div className="text-semibold text-lg">
                          {address.address_line_1}
                          <span className="text-sm">{` ${String(address.city)}, ${String(
                            address.province_name,
                          )}`}</span>
                        </div>
                      </Option>
                    );
                  })}
                </Select>
              </FormLayout.Group>
              <FormLayout.Group>
                <div />
              </FormLayout.Group>
            </FormLayout>
          </Form>
        </Card.Section>
      </Card>
    </Page>
  );
}
